import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import {
  BrainCircuit,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Search,
  GitBranch,
  Send,
  RotateCcw,
  PhoneCall,
  PhoneOff,
  MessageSquare,
  FileText,
  Eye,
  Play,
  Pause,
  Volume2,
} from "lucide-react";

/* ── Config ──────────────────────────────────────────── */
const ENTRY_INTERVAL = 500;
const TYPING_SPEED = 25;

/* ── Fake audio player (for call recording popovers) ── */
const AudioPlayer = ({ label, duration }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!playing) return;
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setPlaying(false);
          return 0;
        }
        return p + 2;
      });
    }, 150);
    return () => clearInterval(interval);
  }, [playing]);

  return (
    <div className="bg-slate-800 rounded-lg p-4 space-y-2.5">
      <div className="flex items-center gap-2.5">
        <Volume2 className="w-4 h-4 text-slate-400" />
        <span className="text-[12px] text-slate-300 font-medium">{label}</span>
        <span className="text-[11px] text-slate-500 ml-auto">{duration}</span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => setPlaying(!playing)}
          className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center hover:bg-blue-400 transition-colors flex-shrink-0"
        >
          {playing ? (
            <Pause className="w-3.5 h-3.5 text-white" />
          ) : (
            <Play className="w-3.5 h-3.5 text-white ml-0.5" />
          )}
        </button>
        <div className="flex-1 h-10 flex items-center gap-[1px]">
          {Array.from({ length: 50 }).map((_, i) => {
            const height = Math.sin(i * 0.35 + 1) * 0.5 + 0.3 + Math.random() * 0.2;
            const filled = i / 50 * 100 <= progress;
            return (
              <div
                key={i}
                className={`flex-1 rounded-full transition-colors duration-100 ${filled ? "bg-blue-400" : "bg-slate-600"}`}
                style={{ height: `${height * 100}%` }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ── Mouse-following popover ─────────────────────────── */
const InspectPopover = ({ detail, mousePos, onClose }) => {
  if (!detail || !mousePos) return null;

  // Position: to the right of cursor, clamped to viewport
  const popoverW = 640;
  const popoverH = 680;
  const x = Math.min(mousePos.x + 20, window.innerWidth - popoverW - 20);
  const y = Math.min(Math.max(mousePos.y - 120, 12), window.innerHeight - popoverH - 20);

  return createPortal(
    <div
      className="fixed z-[99999] pointer-events-auto"
      style={{ left: x, top: y }}
      onMouseLeave={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 4 }}
        transition={{ duration: 0.15 }}
        className="rounded-xl border border-blue-200/60 bg-white shadow-2xl overflow-hidden"
        style={{ width: popoverW }}
      >
        <div className="px-5 py-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-slate-50 flex items-center gap-2.5">
          <Eye className="w-4 h-4 text-blue-500" />
          <span className="text-[13px] font-bold text-blue-600 uppercase tracking-wider">
            {detail.title || "Step Detail"}
          </span>
        </div>
        <div className="p-5 space-y-4 max-h-[620px] overflow-y-auto">
          {detail.sections?.map((section, i) => (
            <div key={i}>
              {section.label && (
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                  {section.label}
                </div>
              )}
              {section.type === "kvList" &&
                section.items?.map((item, j) => (
                  <div key={j} className="flex justify-between text-[13.5px] py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-500">{item.key}</span>
                    <span className="text-slate-800 font-medium text-right max-w-[58%]">{item.value}</span>
                  </div>
                ))}
              {section.type === "text" && (
                <p className="text-[14px] text-slate-600 leading-relaxed">
                  {section.content}
                </p>
              )}
              {section.type === "code" && (
                <pre className="text-[12px] bg-slate-900 text-slate-200 rounded-lg p-4 leading-relaxed overflow-x-auto whitespace-pre-wrap font-mono">
                  {section.content}
                </pre>
              )}
              {section.type === "tags" &&
                section.items && (
                  <div className="flex flex-wrap gap-2">
                    {section.items.map((tag, j) => (
                      <span
                        key={j}
                        className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-600 border border-blue-100"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              {section.type === "file" && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <div>
                    <p className="text-[13px] font-medium text-slate-800">
                      {section.filename}
                    </p>
                    {section.meta && (
                      <p className="text-[11px] text-slate-400">{section.meta}</p>
                    )}
                  </div>
                </div>
              )}
              {section.type === "audio" && (
                <AudioPlayer label={section.label} duration={section.duration} />
              )}
              {section.type === "transcript" && section.lines && (
                <div className="bg-slate-50 rounded-lg border border-slate-200 p-4 space-y-2.5">
                  {section.lines.map((line, j) => (
                    <div key={j} className="flex items-start gap-2.5">
                      <span className={`text-[11px] font-bold uppercase mt-0.5 flex-shrink-0 w-16 ${line.speaker === "Agent" ? "text-blue-500" : "text-slate-400"}`}>
                        {line.speaker}
                      </span>
                      <p className="text-[13px] text-slate-700 leading-relaxed italic">"{line.text}"</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

/* ── Log line renderer ───────────────────────────────── */
const LogLine = ({ entry, isNew, onInspect, onInspectLeave, inspectedIdx, idx }) => {
  const ref = useRef(null);
  const animProps = isNew
    ? { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25 } }
    : { animate: { opacity: 1 } };

  const hasDetail = !!entry.detail;
  const isInspected = inspectedIdx === idx;

  const handleMouseEnter = (e) => {
    if (hasDetail) onInspect(idx, entry.detail, { x: e.clientX, y: e.clientY });
  };
  const handleMouseLeave = () => {
    if (hasDetail) onInspectLeave();
  };

  /* ── Thinking block ── */
  if (entry.type === "thinking") {
    return (
      <motion.div
        {...animProps}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`mx-4 my-2.5 rounded-lg bg-gradient-to-r from-blue-50 to-sky-50/50 border px-4 py-3 transition-all ${
          hasDetail ? "cursor-pointer" : ""
        } ${isInspected ? "border-blue-300 ring-2 ring-blue-100" : "border-blue-100"}`}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <BrainCircuit className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-[11px] font-bold text-blue-400 uppercase tracking-wider">Reasoning</span>
          {hasDetail && (
            <span className="ml-auto text-[10px] text-blue-300 font-medium flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> inspect
            </span>
          )}
        </div>
        <p className="text-[14px] text-blue-600/80 leading-[1.6]">{entry.text}</p>
      </motion.div>
    );
  }

  /* ── Spawn ── */
  if (entry.type === "spawn") {
    return (
      <motion.div {...animProps} className="flex items-center gap-2.5 py-2 px-5 my-1">
        <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
          <GitBranch className="w-4 h-4 text-blue-500" />
        </motion.div>
        <span className="text-[13px] text-blue-600 font-semibold">{entry.text}</span>
        <span className="ml-auto px-2.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-blue-600">SPAWNED</span>
      </motion.div>
    );
  }

  /* ── Call start ── */
  if (entry.type === "call_start") {
    return (
      <motion.div
        {...animProps}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`mx-4 my-2.5 rounded-lg bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 px-4 py-3 ${hasDetail ? "cursor-pointer" : ""} ${isInspected ? "ring-2 ring-blue-200" : ""}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center">
            <PhoneCall className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-[11px] font-bold text-blue-600 uppercase tracking-wider">Outbound Call</span>
          <span className="ml-auto flex items-center gap-1.5">
            <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-[11px] font-semibold text-blue-600">Connected</span>
          </span>
        </div>
        <p className="text-[14px] text-blue-700 font-medium">{entry.text}</p>
        {hasDetail && <span className="text-[10px] text-blue-400 font-medium flex items-center gap-1 mt-1"><Eye className="w-3.5 h-3.5" /> hover to hear recording</span>}
      </motion.div>
    );
  }

  /* ── Call transcript ── */
  if (entry.type === "call_transcript") {
    return (
      <motion.div {...animProps} className="mx-4 my-1">
        <div className="ml-3 pl-3 border-l-2 border-blue-200 py-1">
          <div className="flex items-start gap-2">
            <span className="text-[11px] font-bold uppercase mt-0.5 flex-shrink-0 w-14" style={{ color: entry.speaker === "Agent" || entry.speaker?.length <= 5 ? "#3b82f6" : "#64748b" }}>
              {entry.speaker || "Agent"}
            </span>
            <p className="text-[14px] text-slate-700 leading-[1.55] italic">"{entry.text}"</p>
          </div>
        </div>
      </motion.div>
    );
  }

  /* ── Call end ── */
  if (entry.type === "call_end") {
    return (
      <motion.div
        {...animProps}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`mx-4 my-2 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 flex items-center gap-2.5 ${hasDetail ? "cursor-pointer" : ""} ${isInspected ? "ring-2 ring-blue-200" : ""}`}
      >
        <PhoneOff className="w-4 h-4 text-slate-400" />
        <span className="text-[13px] text-slate-500 font-medium">{entry.text}</span>
        {hasDetail && <span className="ml-auto text-[10px] text-blue-400 font-medium flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> hear recording</span>}
      </motion.div>
    );
  }

  /* ── SMS sent ── */
  if (entry.type === "sms_sent") {
    return (
      <motion.div {...animProps} className="mx-4 my-2 flex justify-end">
        <div className="max-w-[80%]">
          <div className="flex items-center gap-1.5 mb-1 justify-end">
            <MessageSquare className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-[11px] font-bold text-blue-400 uppercase tracking-wider">SMS Sent</span>
          </div>
          <div className="bg-blue-500 text-white rounded-2xl rounded-br-md px-4 py-3 shadow-sm">
            <p className="text-[14px] leading-[1.55]">{entry.text}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  /* ── SMS received ── */
  if (entry.type === "sms_received") {
    return (
      <motion.div {...animProps} className="mx-4 my-2 flex justify-start">
        <div className="max-w-[80%]">
          <div className="flex items-center gap-1.5 mb-1">
            <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{entry.from || "Patient"}</span>
          </div>
          <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md px-4 py-3 border border-slate-200">
            <p className="text-[14px] leading-[1.55]">{entry.text}</p>
          </div>
        </div>
      </motion.div>
    );
  }

  /* ── Standard step / success / warning ── */
  const isSuccess = entry.type === "success";
  const isWarning = entry.type === "warning";

  return (
    <motion.div
      {...animProps}
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`flex items-start gap-2.5 py-[6px] px-5 rounded-md transition-all ${
        hasDetail ? "cursor-pointer hover:bg-blue-50/40" : ""
      } ${isInspected ? "bg-blue-50/60" : ""}`}
    >
      {isSuccess ? (
        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-[3px]" />
      ) : isWarning ? (
        <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-[3px]" />
      ) : (
        <Search className="w-4 h-4 text-slate-400 flex-shrink-0 mt-[3px]" />
      )}
      <span className={`text-[14px] leading-relaxed ${
        isSuccess ? "text-blue-700 font-medium" : isWarning ? "text-amber-700 font-medium" : "text-slate-600"
      }`}>
        {entry.text}
      </span>
      {hasDetail && <Eye className="w-3.5 h-3.5 text-blue-300 flex-shrink-0 mt-[4px] ml-auto" />}
    </motion.div>
  );
};

/* ── Main Component ──────────────────────────────────── */
const AgentReasoningDemo = ({ scenario }) => {
  const { agentName, agentRole, masterPrompt, narrative, entries } = scenario;

  const [phase, setPhase] = useState("idle");
  const [typedChars, setTypedChars] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [inspected, setInspected] = useState(null); // { idx, detail, mousePos }
  const scrollRef = useRef(null);
  const inspectTimeout = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setPhase("typing"), 400);
    return () => clearTimeout(t);
  }, []);

  const handleReplay = useCallback(() => {
    setPhase("idle");
    setTypedChars(0);
    setVisibleCount(0);
    setInspected(null);
    setTimeout(() => setPhase("typing"), 300);
  }, []);

  const handleInspect = useCallback((idx, detail, mousePos) => {
    clearTimeout(inspectTimeout.current);
    setInspected({ idx, detail, mousePos });
  }, []);

  const handleInspectLeave = useCallback(() => {
    inspectTimeout.current = setTimeout(() => setInspected(null), 200);
  }, []);

  useEffect(() => {
    if (phase !== "typing") return;
    if (typedChars >= masterPrompt.length) {
      const t = setTimeout(() => setPhase("streaming"), 600);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setTypedChars((c) => Math.min(c + 2, masterPrompt.length)), TYPING_SPEED);
    return () => clearTimeout(t);
  }, [phase, typedChars, masterPrompt]);

  useEffect(() => {
    if (phase !== "streaming") return;
    if (visibleCount >= entries.length) {
      setPhase("done");
      return;
    }
    const t = setTimeout(() => setVisibleCount((c) => c + 1), ENTRY_INTERVAL);
    return () => clearTimeout(t);
  }, [phase, visibleCount, entries.length]);

  // Auto-scroll: double rAF ensures DOM + framer-motion layout is complete before scrolling
  useEffect(() => {
    if (!scrollRef.current) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
        }
      });
    });
  }, [visibleCount]);

  const visibleEntries = entries.slice(0, visibleCount);
  const isStreaming = phase === "streaming";
  const isDone = phase === "done";

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* ═══ LEFT: Narrative — pinned, independent scroll ═══ */}
      <div className="w-[380px] flex-shrink-0 flex flex-col justify-start pt-10 pb-6 pl-2 pr-6 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[13px] font-semibold mb-4 border shadow-sm bg-blue-50 text-blue-600 border-blue-200">
            <BrainCircuit className="w-4 h-4" />
            <span>Reasoning Demo</span>
          </div>
          <h3 className="text-[28px] font-bold tracking-tight text-slate-900 leading-[1.15] mb-2.5">
            How <span className="text-blue-600">{agentName}</span> thinks
          </h3>
          <p className="text-[14.5px] text-slate-500 leading-relaxed">
            Watch {agentName} reason through a real scenario. Hover any step with an <Eye className="w-4 h-4 inline text-slate-400 -mt-0.5" /> icon to inspect how the AI processed it.
          </p>
        </div>
        <div className="space-y-5">
          {narrative.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.12, duration: 0.4 }}
              className="flex gap-3"
            >
              <div className="w-1.5 rounded-full flex-shrink-0 mt-1 bg-blue-300" style={{ minHeight: 40 }} />
              <div className="min-w-0">
                <h4 className="text-[14px] font-bold text-slate-800 leading-snug mb-1">{item.title}</h4>
                <p className="text-[12.5px] text-slate-500 leading-relaxed">{item.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ═══ RIGHT: Browser graphic ═══ */}
      <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="flex-1 rounded-xl border border-slate-200/80 bg-white shadow-[0_2px_30px_rgba(0,0,0,0.06)] flex flex-col overflow-hidden min-h-0"
        >
          {/* Browser chrome */}
          <div className="flex items-center gap-2 px-5 py-2.5 border-b border-slate-100 bg-slate-50/80 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
              <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <div className="w-3 h-3 rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 flex justify-center">
              <div className="flex items-center gap-2 px-4 py-1 rounded-md bg-white border border-slate-200 shadow-sm">
                <ShieldCheck className="w-3 h-3 text-emerald-500" />
                <span className="text-[12px] text-slate-400 font-medium">app.asha.health</span>
              </div>
            </div>
            {isDone && (
              <button onClick={handleReplay} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <RotateCcw className="w-3.5 h-3.5" />
                Replay
              </button>
            )}
          </div>

          {/* Master prompt bar */}
          <div className="px-5 py-4 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 bg-gradient-to-br from-blue-500 to-blue-600">
                <BrainCircuit className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 flex items-center gap-2 px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 text-[15px] text-slate-700 font-medium">
                <span>
                  {masterPrompt.slice(0, typedChars)}
                  {phase === "typing" && typedChars < masterPrompt.length && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                      className="inline-block w-[2px] h-4 bg-blue-500 ml-[1px] align-middle"
                    />
                  )}
                </span>
                <Send className="w-4 h-4 text-blue-400 ml-auto flex-shrink-0" />
              </div>
            </div>
            {(isStreaming || isDone) && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 mt-2.5 ml-[52px]">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-blue-400" />
                  <span className="text-[13px] text-slate-500">
                    Agent: <span className="font-semibold text-blue-600">{agentName}</span> — {agentRole}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 border border-blue-200/50">
                  <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  <span className="text-[10px] font-bold text-blue-600 tracking-wide">{isDone ? "COMPLETE" : "LIVE"}</span>
                </div>
                <span className="text-[12px] text-slate-400 ml-auto tabular-nums">{visibleCount}/{entries.length} steps</span>
              </motion.div>
            )}
          </div>

          {/* Reasoning stream */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto py-2 min-h-0">
            {isStreaming || isDone ? (
              <div className="space-y-[2px]">
                {visibleEntries.map((entry, i) => (
                  <LogLine
                    key={i}
                    idx={i}
                    entry={entry}
                    isNew={i === visibleEntries.length - 1 && isStreaming}
                    onInspect={handleInspect}
                    onInspectLeave={handleInspectLeave}
                    inspectedIdx={inspected?.idx}
                  />
                ))}
                {isStreaming && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2.5 py-1.5 px-5 mt-1">
                    <Loader2 className="w-4.5 h-4.5 text-blue-500 animate-spin" />
                    <span className="text-[13px] text-blue-500 font-medium">Reasoning...</span>
                  </motion.div>
                )}
                {isDone && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mx-4 my-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 flex items-center gap-2.5"
                  >
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-[14px] text-blue-700 font-semibold">Task complete — all actions executed autonomously</span>
                  </motion.div>
                )}
              </div>
            ) : phase === "typing" ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-2 text-blue-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-base font-medium">Processing instruction...</span>
                </div>
              </div>
            ) : null}
          </div>
        </motion.div>
      </div>

      {/* ═══ Mouse-following inspect popover ═══ */}
      <AnimatePresence>
        {inspected && (
          <InspectPopover
            detail={inspected.detail}
            mousePos={inspected.mousePos}
            onClose={() => {
              clearTimeout(inspectTimeout.current);
              setInspected(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AgentReasoningDemo;
